/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import {
  ExpandableOverviewItem,
  Button,
  Table,
  Notification
} from '../'

import { LoanDetails } from './LoanDetails'

import {
  ReadOnlyContext,
} from '../../context'


const initialiseLoanState = (props) => {
  let loanExists = props.credit !== null

  // check and add eligible loans not saved yet
  if (!loanExists && props.amountEligibleFor) {
    return {
      id: null,
      amount: null,
      instalmentAgreed: null,
      cmsId: null,
      creditType: props.creditType
    }
  } else if (loanExists) {
    return {
      id: props.credit.id,
      amount: props.credit.amount,
      instalmentAgreed: props.credit.instalmentAgreed,
      cmsId: props.credit.cmsId,
      creditType: props.creditType
    }
  }
}


export default class CreditApplicationRequestedLoan extends React.PureComponent {
  static propTypes = {
    credit: PropTypes.shape({
      id: PropTypes.string,
      creditType: PropTypes.string,
      amount: PropTypes.string,
      balance: PropTypes.number,
      instalmentAgreed: PropTypes.string,
      cmsId: PropTypes.string
    }),
    creditType: PropTypes.string,
    amountEligibleFor: PropTypes.string,
    createCredit: PropTypes.func.isRequired,
    updateCredit: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  state = {
    editLoan: false,
    savingLoan: false,
    loan: initialiseLoanState(this.props),
    savedLoan: this.props.credit,
    duplicateCmsId: null
  }

  attachCmsIdToErrorObj = (error, cmsId) => {
    let err = Object.assign({}, error)
    err.cmsId = cmsId
    return Promise.reject(err)
  }

  saveLoanDetails = () => {
    this.setState({ savingLoan: true })

    let savedLoan = this.props.credit

    if (savedLoan) {
      let { amount, instalmentAgreed, cmsId } = this.state.loan
      // to save changes to existing loan
      let input = { id: this.props.credit.id }
      if (amount && amount !== parseFloat(savedLoan.amount)) {
        input.amount = amount.toString()
      }
      if (instalmentAgreed && instalmentAgreed !== parseFloat(savedLoan.instalmentAgreed)) {
        input.instalmentAgreed = instalmentAgreed.toString()
        input.instalmentCurrent = instalmentAgreed.toString()  // changing instalmentAgreed also sets instalmentCurrent
      }
      if (cmsId && cmsId !== savedLoan.cmsId) {
        input.cmsId = cmsId
      }

      if (input.cmsId) {
        this.props.updateCredit(input)
          .then(({ data }) => {
            this.setState({ editLoan: false, savingLoan: false }, this.props.refetchData)
          })
          .catch(error => this.attachCmsIdToErrorObj(error, input.cmsId))
      } else {
        this.props.updateCredit(input)
          .then(({ data }) => {
            this.setState({ editLoan: false, savingLoan: false }, this.props.refetchData)
          })
      }
    } else if (this.state.loan.amount) {
      let { amount, instalmentAgreed, cmsId } = this.state.loan
      // to create new loan
      let input = {
        creditType: this.props.creditType.toLowerCase(),
        amount: amount.toString()
      }
      if (instalmentAgreed) {
        input.instalmentAgreed = instalmentAgreed.toString()
        // NOTE: on loan creation, instalmentCurrent gets set in backend to equal instalmentAgreed as part of save override
      }
      if (cmsId) {
        input.cmsId = cmsId
      }

      this.props.createCredit(input)
        .then(({ data }) => {
          let loan = Object.assign({}, this.state.loan)
          loan.id = data.creditMutate.credit.id
          this.setState({ loan, editLoan: false, savingLoan: false }, this.props.refetchData)
        })
        .catch(error => this.attachCmsIdToErrorObj(error, input.cmsId))
    }
  }

  handleErrorOnDuplicateLmsId = (error) => {
    if (error.hasOwnProperty('graphQLErrors') && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message.includes('Loan with this Loan Management System ID already exists.')) {
      // now get error message nested in error object
      let errorLmsIdDuplicate = 'Loan with Loan Management System ID already exists.'
      this.setState({ errorLmsIdDuplicate, duplicateCmsId: error.cmsId })
    }
  }

  handleLoanFieldChange = (e) => {
    let field = e.target.id
    let value = field === 'cmsId'
      ? e.target.value
      : e.target.value ? parseFloat(e.target.value) : null

    let loan = { ...this.state.loan, ...{ [field]: value } }

    this.setState(prevState => (
      prevState.errorLmsIdDuplicate
        ? {
            loan,
            errorLmsIdDuplicate: null,
            duplicateCmsId: null
          }
        : { loan }
    ))
  }

  editLoan = () => this.setState({ editLoan: true })

  loanChanged = () => !isEqual(this.props.credit, this.state.loan)

  clearLoanInputsAndExitEditMode = () => {
    this.setState({ loans: initialiseLoanState(this.props), errorLmsIdDuplicate: null, editLoan: false })
  }

  render() {
    const { credit, amountEligibleFor, open, onClick, className } = this.props
    const loanRequested = credit && credit.amount && credit.instalmentAgreed && credit.cmsId
    const noLoanAppliedFor = credit === null

    return (
      <ExpandableOverviewItem
        title='Requested loan'
        summary={noLoanAppliedFor
          ? 'No loan applied for'
          : loanRequested
            ? 'Loan requested'
            : ''
        }
        iconSymbol={loanRequested ? 'check' : 'warning'}
        iconColour={loanRequested ? 'success' : 'warning'}
        open={open}
        locked={!amountEligibleFor}
        onClick={onClick}
        className={className}>
        <div className='slds-p-bottom_large'>
          {this.state.errorLmsIdDuplicate &&
            <Notification
              type='error'
              message={this.state.errorLmsIdDuplicate}
              onCloseClick={() => this.setState({ errorLmsIdDuplicate: null })}
              className='slds-m-bottom_small'
              closeIcon={false}
            />
          }
          <Table
            columns={[ 'type', 'eligible for', 'amount', 'instalment', 'lms id']}
            rows={this.state.loan
              ? [
                  <LoanDetails
                    loan={this.state.loan.id}
                    creditType={this.props.creditType}
                    eligibleFor={this.props.amountEligibleFor}
                    amount={this.state.loan.amount}
                    instalment={this.state.loan.instalmentAgreed}
                    cmsId={this.state.loan.cmsId}
                    duplicateCmsId={this.state.duplicateCmsId}
                    handleChange={this.handleLoanFieldChange}
                    edit={this.state.editLoan}
                    key='loanDetails_'
                  />
                ]
              : []
            }
            objectRows={true}
            noCellPadding={true}
          />
          <div className='slds-grid slds-m-top_small'>
            <ReadOnlyContext.Consumer>
              {readOnly => (
                this.state.editLoan
                  ? <div className='slds-col_bump-left'>
                      <Button minWidth={90} onClick={this.clearLoanInputsAndExitEditMode}>
                        Cancel
                      </Button>
                      <Button
                        type='brand'
                        minWidth={90}
                        disabled={!this.loanChanged()}
                        onClick={this.saveLoanDetails}>
                        {this.state.saving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  : <div className='slds-col_bump-left'>
                      <Button minWidth={90} onClick={this.editLoan} disabled={readOnly}>
                        Edit
                      </Button>
                    </div>
              )}
            </ReadOnlyContext.Consumer>
          </div>
        </div>
      </ExpandableOverviewItem>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-requested-loan/index.js